import React, { forwardRef } from 'react'
import { useIntl } from 'react-intl'

// types
import { InputTriggerProps } from './types'

// styles
import * as SC from './InputTriggerStyles'

// assets
import CloseIcon from '../../assets/icons/CloseIcon'

const InputTrigger = forwardRef<HTMLButtonElement, InputTriggerProps>((props, ref) => {
	const { formatMessage } = useIntl()

	const {
		id,
		className,
		labelName,
		value,
		placeholder,
		prefixIcon,
		suffixIcon,
		allowClear = false,
		disabled,
		invalid,
		fieldStyle = 'default',
		expanded = false,
		hasPopup = 'dialog',
		controlsId,
		labelledBy,
		ariaLabel,
		onClick,
		onFocus,
		onKeyDown,
		onClear
	} = props

	const hasValue = value != null && value !== ''
	const showClear = allowClear && hasValue && !disabled && Boolean(onClear)
	const hasSuffix = showClear || Boolean(suffixIcon)

	const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		event.stopPropagation()
		onClear?.(event)
	}

	return (
		<SC.Root className={className}>
			{prefixIcon && <SC.PrefixIconWrapper $isDisabled={disabled}>{prefixIcon}</SC.PrefixIconWrapper>}
			<SC.Trigger
				ref={ref}
				id={id}
				type='button'
				disabled={disabled}
				aria-haspopup={hasPopup}
				aria-expanded={expanded}
				aria-controls={controlsId}
				aria-labelledby={ariaLabel ? undefined : labelledBy}
				aria-label={ariaLabel}
				$fieldStyle={fieldStyle}
				$hasPrefix={Boolean(prefixIcon)}
				$hasSuffix={hasSuffix}
				$isDisabled={disabled}
				$isInvalid={invalid}
				$isExpanded={expanded}
				onClick={onClick}
				onFocus={onFocus}
				onKeyDown={onKeyDown}
			>
				<SC.TriggerValue $isPlaceholder={!hasValue} $isDisabled={disabled}>
					{hasValue ? value : placeholder}
				</SC.TriggerValue>
			</SC.Trigger>
			{showClear ? (
				<SC.ClearButton
					type='button'
					aria-label={formatMessage({ id: 'Clear value for: {labelName}', defaultMessage: 'Clear value for: {labelName}' }, { labelName })}
					onClick={handleClear}
				>
					<CloseIcon />
				</SC.ClearButton>
			) : (
				suffixIcon && <SC.SuffixIconWrapper $isDisabled={disabled}>{suffixIcon}</SC.SuffixIconWrapper>
			)}
		</SC.Root>
	)
})

InputTrigger.displayName = 'InputTrigger'

export default InputTrigger
